import React from 'react';
import { Theme, ThemeProvider } from '@material-ui/core';

import { GlobalState } from 'ha/types/store';
import { asyncConnect, AsyncItems } from 'ha/modules/ReduxConnect';
import { HOME } from 'ha/constants/pageNames';
import Page from 'ha/modules/Page';
import { load as loadFeaturedCities } from 'ha/modules/FeaturedCities/actions';
import loadSavedSearches from 'ha/modules/SavedSearches/actions/loadSavedSearches';
import { getUserId } from 'ha/modules/AuthLogic/selectors';
import defaultFont from 'ha/constants/fonts/default';
import sharpSansHeadersFont from 'ha/constants/fonts/sourceSansHeaders';
import { loadHermesData, useOptionalHermes } from 'ha/modules/Hermes';

import HomeLayout from '../components/HomeLayout';
import { TopCitiesNameList } from '../constants';
import { getCities, getCounts } from '../actions';
import { getCities as getCitiesSelector } from '../selectors';
import { HermesData } from '../types';

const asyncPageItems: AsyncItems<{}, GlobalState> = [
  {
    key: 'home',
    promise: ({
      store: { dispatch, getState },
      intl: {
        urlResolver: { getHomePageDataUrl },
        lang,
      },
    }) => {
      const state = getState();
      const userId = getUserId(state);

      return Promise.all([
        userId
          ? dispatch(loadSavedSearches(userId, { language: lang }))
          : Promise.resolve(),
        dispatch(loadFeaturedCities()),
        dispatch(getCities(TopCitiesNameList, lang)).then(() => {
          const cities = getCitiesSelector(getState());

          return dispatch(getCounts(cities));
        }),
        dispatch(loadHermesData(getHomePageDataUrl(lang))),
      ]);
    },
  },
];

// to avoid new theme creation each rerender
const homeNextTheme: (outerTheme: Theme) => Theme = outerTheme => ({
  ...outerTheme,
  typography: {
    ...outerTheme.typography,
    h1: {
      ...outerTheme.typography.h1,
      fontFamily: sharpSansHeadersFont.fontFamily,
    },
    h2: {
      ...outerTheme.typography.h2,
      fontFamily: sharpSansHeadersFont.fontFamily,
    },
    h3: {
      ...outerTheme.typography.h3,
      fontFamily: sharpSansHeadersFont.fontFamily,
    },
  },
});

const HomePageContainer: React.FC = () => {
  const { data } = useOptionalHermes<HermesData>();

  return (
    <Page
      name={HOME}
      noFollowFooter={false}
      activeFonts={[defaultFont, sharpSansHeadersFont]}
      remarketingTag={{
        hrental_id: '',
        hrental_pagetype: 'homepage',
        hrental_startdate: '',
        hrental_enddate: '',
        hrental_totalvalue: '',
      }}
    >
      <ThemeProvider theme={homeNextTheme}>
        <HomeLayout
          promotedContentTitle={data?.promotedContentTitle || ''}
          promotedContent={data?.promotedContent || []}
        />
      </ThemeProvider>
    </Page>
  );
};

export default asyncConnect(asyncPageItems)(HomePageContainer);
